import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, RefreshCw, Key, Mail, AlertTriangle, RotateCcw, UserX, Shield, Eye, EyeOff, Search, X, Building2, Home, GraduationCap, Award, FileText } from 'lucide-react';
import { API_URL } from '../config';
import FicheRHEmployee from './FicheRHEmployee';

const Employees = ({
  authUser,
  employees,
  roles,
  services,
  loading,
  showAddEmployee,
  setShowAddEmployee,
  editingEmployee,
  setEditingEmployee,
  employeeForm,
  setEmployeeForm,
  handleAddEmployee,
  handleUpdateEmployee,
  handleDeleteEmployee,
  resetEmployeeForm,
  startEdit,
  authToken,
  pointages = [],
  schedules = []
}) => {
  // Seuls Patron et Super Manager peuvent ajouter des employés
  const canAddEmployees = ['Patron', 'Super Manager'].includes(authUser?.role_name);
  const isAdmin = ['Patron', 'Super Manager'].includes(authUser?.role_name);
  
  // Patron, Super Manager et Manager peuvent voir la liste (mais pas forcément ajouter)
  const canViewEmployees = ['Patron', 'Super Manager', 'Manager'].includes(authUser?.role_name);

  // States pour la gestion admin
  const [viewMode, setViewMode] = useState('active');
  const [deletedEmployees, setDeletedEmployees] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [passwordMode, setPasswordMode] = useState('email');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  // Modale de confirmation de suppression définitive
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(null); // { id, name } ou null
  const [ficheRHEmployeeId, setFicheRHEmployeeId] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  // Compétences
  const [expandedCompEmpId, setExpandedCompEmpId] = useState(null);
  const [empCompetences, setEmpCompetences] = useState([]);
  const [competencesRef, setCompetencesRef] = useState([]);
  const [loadingComp, setLoadingComp] = useState(false);
  const [showAddCompModal, setShowAddCompModal] = useState(false);
  const [newCompData, setNewCompData] = useState({ competence: '', niveau: 'intermediaire', certifie: false });
  const [searchFilter, setSearchFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [empPage, setEmpPage] = useState(1);
  const [extrasPage, setExtrasPage] = useState(1);
  const EMP_PER_PAGE = 10;

  // Obtenir le nom du rôle sélectionné
  const selectedRoleName = roles.find(r => r.id === parseInt(employeeForm.role_id))?.name || '';

  // Vérifier si un employé spécifique peut être modifié
  const canEditEmployee = (employee) => {
    if (authUser?.role_name === 'Patron') return true;
    if (authUser?.role_name === 'Super Manager' && employee.role_name !== 'Patron') return true;
    if (authUser?.role_name === 'Manager' && !['Patron', 'Super Manager'].includes(employee.role_name)) return true;
    return false;
  };

  // Charger les employés supprimés
  const loadDeletedEmployees = useCallback(async () => {
    if (!isAdmin || !authToken) return;
    setLoadingDeleted(true);
    try {
      const response = await fetch(`${API_URL}/admin/employees/deleted`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDeletedEmployees(data);
      }
    } catch (err) {
      console.error('Erreur chargement employés supprimés:', err);
    } finally {
      setLoadingDeleted(false);
    }
  }, [isAdmin, authToken]);

  useEffect(() => {
    if (viewMode === 'deleted') {
      loadDeletedEmployees();
    }
  }, [viewMode, loadDeletedEmployees]);

  // Charger le référentiel de compétences (une seule fois)
  useEffect(() => {
    if (!authToken) return;
    fetch(`${API_URL}/competences-referentiel`, {
      headers: { 'Authorization': `Bearer ${authToken}` }
    }).then(r => r.ok ? r.json() : []).then(setCompetencesRef).catch(() => {});
  }, [authToken]);

  // Charger les compétences d'un employé
  const loadCompetences = useCallback(async (empId) => {
    setLoadingComp(true);
    try {
      const res = await fetch(`${API_URL}/employees/${empId}/competences`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (res.ok) setEmpCompetences(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoadingComp(false); }
  }, [authToken]);

  const toggleCompetences = (empId) => {
    if (expandedCompEmpId === empId) {
      setExpandedCompEmpId(null);
      setEmpCompetences([]);
    } else {
      setExpandedCompEmpId(empId);
      loadCompetences(empId);
    }
  };

  const handleAddCompetence = async () => {
    if (!newCompData.competence || !expandedCompEmpId) return;
    try {
      const res = await fetch(`${API_URL}/employees/${expandedCompEmpId}/competences`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newCompData)
      });
      if (res.ok) {
        setShowAddCompModal(false);
        setNewCompData({ competence: '', niveau: 'intermediaire', certifie: false });
        loadCompetences(expandedCompEmpId);
      } else { const err = await res.json(); alert(err.error); }
    } catch (err) { alert('Erreur : ' + err.message); }
  };

  const handleDeleteCompetence = async (compId) => {
    if (!window.confirm('Retirer cette compétence ?')) return;
    try {
      await fetch(`${API_URL}/employees/${expandedCompEmpId}/competences/${compId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      loadCompetences(expandedCompEmpId);
    } catch (err) { alert('Erreur : ' + err.message); }
  };

  const NIVEAU_COLORS = {
    debutant: 'bg-elevated-1 text-text-secondary',
    intermediaire: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    confirme: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
    expert: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
  };

  // Restaurer un employé supprimé
  const handleRestore = async (id) => {
    if (!window.confirm('Restaurer cet employé ? Son compte sera réactivé.')) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/employees/${id}/restore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await response.json();
      if (response.ok) {
        setActionMessage({ type: 'success', text: data.message });
        loadDeletedEmployees();
      } else {
        setActionMessage({ type: 'error', text: data.error });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Erreur réseau' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setActionMessage(null), 5000);
    }
  };

  // Suppression définitive
  const handleHardDelete = async (id, name) => {
    const confirmText = `Supprimer DÉFINITIVEMENT ${name} ?\n\nCette action est IRRÉVERSIBLE.\nToutes les données associées seront supprimées.`;
    if (!window.confirm(confirmText)) return;
    // Double confirmation
    if (!window.confirm('Êtes-vous vraiment sûr ? Cette action ne peut PAS être annulée.')) return;

    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/employees/${id}/hard-delete`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await response.json();
      if (response.ok) {
        setActionMessage({ type: 'success', text: data.message });
        loadDeletedEmployees();
      } else {
        setActionMessage({ type: 'error', text: data.error });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Erreur réseau' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setActionMessage(null), 5000);
    }
  };

  // Changer le mot de passe
  const handleChangePassword = async () => {
    if (!showPasswordModal) return;
    setActionLoading(true);

    const body = {};
    if (passwordMode === 'email') {
      body.send_reset_email = true;
    } else {
      if (!newPassword || newPassword.length < 6) {
        setActionMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins 6 caractères' });
        setActionLoading(false);
        return;
      }
      if (newPassword !== confirmPassword) {
        setActionMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
        setActionLoading(false);
        return;
      }
      body.new_password = newPassword;
    }

    try {
      const response = await fetch(`${API_URL}/admin/employees/${showPasswordModal.id}/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (response.ok) {
        setActionMessage({ type: 'success', text: data.message });
        setShowPasswordModal(null);
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setActionMessage({ type: 'error', text: data.error });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Erreur réseau' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setActionMessage(null), 5000);
    }
  };

  // Calculer le statut de présence basé sur les pointages et le planning d'aujourd'hui
  const today = new Date().toISOString().split('T')[0];
  const todayPointagesActifs = pointages.filter(p => p.date === today && p.statut === 'en_cours');
  const presentEmployeeIds = new Set(todayPointagesActifs.map(p => p.user_id));
  const todaySchedules = schedules.filter(s => s.date === today);

  // Enrichir les employés avec le statut de présence dynamique
  const employeesWithStatus = employees.map(emp => {
    const isPresent = presentEmployeeIds.has(emp.id);
    const schedule = todaySchedules.find(s => s.employee_id === emp.id);
    const entryType = schedule?.entry_type || schedule?.shift_type || null;
    const isFormation = entryType === 'Formation';
    const isTeletravail = entryType === 'Télétravail';

    let status, statusColor;
    if (isFormation) {
      status = 'Formation'; statusColor = 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300';
    } else if (isTeletravail) {
      status = 'Télétravail'; statusColor = 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300';
    } else if (isPresent) {
      status = 'Présent'; statusColor = 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
    } else {
      status = 'Absent'; statusColor = 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300';
    }
    return { ...emp, status, statusColor };
  });

  // Filtrer les employés selon la recherche, service et rôle
  const filteredEmployees = employeesWithStatus.filter(emp => {
    if (searchFilter) {
      const search = searchFilter.toLowerCase();
      const match = (
        emp.first_name?.toLowerCase().includes(search) ||
        emp.last_name?.toLowerCase().includes(search) ||
        emp.email?.toLowerCase().includes(search) ||
        emp.role_name?.toLowerCase().includes(search) ||
        emp.service_name?.toLowerCase().includes(search)
      );
      if (!match) return false;
    }
    if (serviceFilter && emp.service_name !== serviceFilter) return false;
    if (roleFilter && emp.role_name !== roleFilter) return false;
    return true;
  });

  // Séparer personnel régulier et Extras
  const regularStaff = filteredEmployees.filter(emp => emp.role_name !== 'Extra');
  const extrasStaff = filteredEmployees.filter(emp => emp.role_name === 'Extra');

  // Pagination du personnel régulier
  const empTotalPages = Math.max(1, Math.ceil(regularStaff.length / EMP_PER_PAGE));
  const pagedRegularStaff = regularStaff.slice((empPage - 1) * EMP_PER_PAGE, empPage * EMP_PER_PAGE);

  // Pagination des Extras (séparée pour ne pas perturber l'affichage du personnel régulier)
  const extrasTotalPages = Math.max(1, Math.ceil(extrasStaff.length / EMP_PER_PAGE));
  const pagedExtrasStaff = extrasStaff.slice((extrasPage - 1) * EMP_PER_PAGE, extrasPage * EMP_PER_PAGE);

  const filteredDeleted = deletedEmployees.filter(emp => {
    if (!searchFilter) return true;
    const search = searchFilter.toLowerCase();
    return (
      emp.first_name?.toLowerCase().includes(search) ||
      emp.last_name?.toLowerCase().includes(search) ||
      emp.email?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6">
      {/* Message d'action */}
      {actionMessage && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          actionMessage.type === 'success' 
            ? 'bg-green-50 dark:bg-green-950/40 border border-green-200 text-green-800 dark:text-green-200' 
            : 'bg-red-50 dark:bg-red-950/40 border border-red-200 text-red-800 dark:text-red-200'
        }`}>
          {actionMessage.type === 'success' ? '✅' : '❌'}
          <span className="font-medium">{actionMessage.text}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-text-primary">Gestion des Employés</h2>
          {!canAddEmployees && canViewEmployees && (
            <p className="text-sm text-text-muted mt-1">
              Seuls les Patrons et Super Managers peuvent ajouter des employés
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {canAddEmployees && (
            <button
              onClick={() => {
                resetEmployeeForm();
                setEditingEmployee(null);
                setShowAddEmployee(true);
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 sm:px-6 py-2.5 sm:py-3 min-h-[44px] rounded-lg flex items-center justify-center space-x-2 transition shadow-md"
            >
              <Plus className="w-5 h-5" />
              <span>Ajouter</span>
            </button>
          )}
        </div>
      </div>

      {/* Onglets Actifs / Supprimés (Patron/SM uniquement) */}
      {isAdmin && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap">
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('active')}
              className={`flex items-center gap-2 px-4 py-2 min-h-[44px] rounded-lg font-semibold transition ${
                viewMode === 'active'
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-elevated-1 text-text-secondary hover:bg-elevated-1'
              }`}
            >
              <Shield className="w-4 h-4" />
              <span>Actifs ({employees.length})</span>
            </button>
            <button
              onClick={() => setViewMode('deleted')}
              className={`flex items-center gap-2 px-4 py-2 min-h-[44px] rounded-lg font-semibold transition ${
                viewMode === 'deleted'
                  ? 'bg-red-500 text-white shadow-md'
                  : 'bg-elevated-1 text-text-secondary hover:bg-elevated-1'
              }`}
            >
              <UserX className="w-4 h-4" />
              <span>Supprimés ({deletedEmployees.length})</span>
            </button>
          </div>

          {/* Recherche */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Rechercher un employé..."
              value={searchFilter}
              onChange={(e) => { setSearchFilter(e.target.value); setEmpPage(1); }}
              className="w-full pl-10 pr-8 py-2 min-h-[44px] border border-border-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
            />
            {searchFilter && (
              <button onClick={() => setSearchFilter('')} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-secondary min-w-[44px] min-h-[44px] flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Filtre Service */}
            <select
              value={serviceFilter}
              onChange={e => { setServiceFilter(e.target.value); setEmpPage(1); }}
              className="px-3 py-2 min-h-[44px] border border-border-strong rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Tous les services</option>
              {services.filter(s => s.name !== 'Extra').map(s => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </select>
            {/* Filtre Rôle */}
            <select
              value={roleFilter}
              onChange={e => { setRoleFilter(e.target.value); setEmpPage(1); }}
              className="px-3 py-2 min-h-[44px] border border-border-strong rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Tous les rôles</option>
              {roles.map(r => (
                <option key={r.id} value={r.name}>{r.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Formulaire d'ajout/édition — fermeture uniquement via la croix ou Annuler */}
      {canAddEmployees && showAddEmployee && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface">
            <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <h3 className="text-lg font-bold text-text-primary">
                {editingEmployee ? 'Modifier l\'employé' : 'Nouvel employé'}
              </h3>
              <button onClick={() => { setShowAddEmployee(false); setEditingEmployee(null); resetEmployeeForm(); }} className="text-text-muted hover:text-text-secondary p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={editingEmployee ? handleUpdateEmployee : handleAddEmployee} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Prénom *</label>
                    <input
                      type="text"
                      required
                      value={employeeForm.first_name}
                      onChange={(e) => setEmployeeForm({...employeeForm, first_name: e.target.value})}
                      className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Nom *</label>
                    <input
                      type="text"
                      required
                      value={employeeForm.last_name}
                      onChange={(e) => setEmployeeForm({...employeeForm, last_name: e.target.value})}
                      className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={employeeForm.email}
                      onChange={(e) => setEmployeeForm({...employeeForm, email: e.target.value})}
                      className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Téléphone</label>
                    <input
                      type="tel"
                      value={employeeForm.phone}
                      onChange={(e) => setEmployeeForm({...employeeForm, phone: e.target.value})}
                      className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Rôle *
                      {authUser?.role_name === 'Manager' && (
                        <span className="text-xs text-text-muted ml-2">(modification réservée à la direction)</span>
                      )}
                    </label>
                    <select
                      required
                      value={employeeForm.role_id}
                      onChange={(e) => setEmployeeForm({...employeeForm, role_id: e.target.value})}
                      disabled={authUser?.role_name === 'Manager'}
                      className={`w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        authUser?.role_name === 'Manager' ? 'bg-elevated-1 cursor-not-allowed' : ''
                      }`}
                    >
                      <option value="">Sélectionnez un rôle</option>
                      {roles.map(role => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                    </select>
                  </div>
                  {selectedRoleName !== 'Extra' && (
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Service *</label>
                      <select
                        required
                        value={employeeForm.service_id}
                        onChange={(e) => setEmployeeForm({...employeeForm, service_id: e.target.value})}
                        className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Sélectionnez un service</option>
                        {services.filter(s => s.name !== 'Extra').map(service => (
                          <option key={service.id} value={service.id}>{service.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {selectedRoleName === 'Extra' && (
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Service</label>
                      <div className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-subtle rounded-lg bg-elevated-1 text-text-secondary">
                        Intervenant externe (pas de service fixe)
                      </div>
                    </div>
                  )}
                  {selectedRoleName !== 'Extra' && (
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Contrat hebdomadaire</label>
                      <select
                        value={employeeForm.heures_hebdomadaires}
                        onChange={(e) => setEmployeeForm({...employeeForm, heures_hebdomadaires: parseInt(e.target.value)})}
                        className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value={35}>35 heures / semaine</option>
                        <option value={39}>39 heures / semaine</option>
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Date d'embauche</label>
                    <input
                      type="date"
                      value={employeeForm.date_embauche || ''}
                      onChange={(e) => setEmployeeForm({...employeeForm, date_embauche: e.target.value})}
                      className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-text-muted mt-1">Utilisé pour le calcul du solde CP et le bonus ancienneté HCR</p>
                  </div>
                  {selectedRoleName === 'Extra' && (
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Contrat</label>
                      <div className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-subtle rounded-lg bg-elevated-1 text-text-secondary">
                        Rémunération à la prestation (forfait extra)
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddEmployee(false);
                    setEditingEmployee(null);
                    resetEmployeeForm();
                  }}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-gray-300 hover:bg-gray-400 text-text-primary transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center space-x-2 transition disabled:opacity-50"
                >
                  <Save className="w-5 h-5" />
                  <span>{editingEmployee ? 'Mettre à jour' : 'Ajouter'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== VUE EMPLOYÉS ACTIFS ===== */}
      {viewMode === 'active' && (
        <>
          {/* Desktop: Table view */}
          <div className="bg-surface rounded-2xl shadow-md border border-border-subtle overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-700 to-slate-800 text-white">
                    <th className="text-left py-3.5 px-6 font-semibold text-sm tracking-wide">Employé</th>
                    <th className="text-left py-3.5 px-6 font-semibold text-sm tracking-wide">Rôle</th>
                    <th className="text-left py-3.5 px-6 font-semibold text-sm tracking-wide">Service</th>
                    <th className="text-left py-3.5 px-6 font-semibold text-sm tracking-wide">Contact</th>
                    <th className="text-left py-3.5 px-6 font-semibold text-sm tracking-wide">Statut</th>
                    <th className="text-left py-3.5 px-6 font-semibold text-sm tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {pagedRegularStaff.map((emp, idx) => (
                    <React.Fragment key={emp.id}>
                    <tr className={`hover:bg-blue-50/40 transition-colors ${idx % 2 === 0 ? 'bg-surface' : 'bg-slate-50/40'}`}>
                      <td className="py-3.5 px-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
                            {emp.avatar}
                          </div>
                          <div>
                            <p className="font-semibold text-text-primary text-sm">{emp.first_name} {emp.last_name}</p>
                            {emp.heures_hebdomadaires && <p className="text-xs text-text-muted">{emp.heures_hebdomadaires}h/sem</p>}
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-6">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                          emp.role_name === 'Patron' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' :
                          emp.role_name === 'Super Manager' ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300' :
                          emp.role_name === 'Manager' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' :
                          'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                        }`}>
                          {emp.role_name}
                        </span>
                      </td>
                      <td className="py-3.5 px-6">
                        {emp.role_name === 'Extra' ? (
                          <span className="text-text-muted italic text-xs">Externe</span>
                        ) : (
                          <span className="text-text-secondary text-sm font-medium">{emp.service_name}</span>
                        )}
                      </td>
                      <td className="py-3.5 px-6">
                        <p className="text-sm text-text-secondary">{emp.email}</p>
                        <p className="text-xs text-text-muted mt-0.5">{emp.phone || '—'}</p>
                      </td>
                      <td className="py-3.5 px-6">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${emp.statusColor}`}>
                          {emp.status}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        {canEditEmployee(emp) ? (
                          <div className="flex space-x-1.5">
                            <button
                              onClick={() => startEdit(emp)}
                              className="text-blue-600 hover:text-blue-700 p-2.5 hover:bg-blue-50 dark:bg-blue-950/40 rounded transition min-w-[36px] min-h-[36px] flex items-center justify-center"
                              title="Modifier"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            {isAdmin && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowPasswordModal(emp);
                                  setPasswordMode('email');
                                  setNewPassword('');
                                  setConfirmPassword('');
                                }}
                                className="text-amber-600 hover:text-amber-700 p-2.5 hover:bg-amber-50 dark:bg-amber-950/40 rounded transition min-w-[36px] min-h-[36px] flex items-center justify-center"
                                title="Changer le mot de passe"
                              >
                                <Key className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => setConfirmDeleteModal({ id: emp.id, name: `${emp.first_name} ${emp.last_name}` })}
                              disabled={loading}
                              className="text-red-600 hover:text-red-700 p-2.5 hover:bg-red-50 dark:bg-red-950/40 rounded transition disabled:opacity-50 min-w-[36px] min-h-[36px] flex items-center justify-center"
                              title="Supprimer définitivement"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-text-muted text-sm italic">Réservé à la direction</span>
                        )}
                        <button
                          onClick={() => toggleCompetences(emp.id)}
                          className={`mt-1 flex items-center gap-1 text-xs px-2 py-1 rounded transition ${expandedCompEmpId === emp.id ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' : 'text-text-muted hover:bg-elevated-1'}`}
                          title="Compétences"
                        >
                          <Award className="w-3 h-3" /> Compétences
                        </button>
                      </td>
                    </tr>
                    {/* Panneau compétences étendu */}
                    {expandedCompEmpId === emp.id && (
                      <tr>
                        <td colSpan={6} className="bg-purple-50/50 px-6 py-3 border-b border-purple-100">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-sm text-purple-800 dark:text-purple-200">Compétences — {emp.first_name} {emp.last_name}</span>
                            {isAdmin && (
                              <button onClick={() => { setShowAddCompModal(true); setNewCompData({ competence: '', niveau: 'intermediaire', certifie: false }); }}
                                className="flex items-center gap-1 px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 transition">
                                <Plus className="w-3 h-3" /> Ajouter
                              </button>
                            )}
                          </div>
                          {loadingComp ? (
                            <p className="text-sm text-text-muted">Chargement...</p>
                          ) : empCompetences.length === 0 ? (
                            <p className="text-sm text-text-muted italic">Aucune compétence enregistrée</p>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {empCompetences.map(c => (
                                <span key={c.id} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${NIVEAU_COLORS[c.niveau] || NIVEAU_COLORS.intermediaire}`}>
                                  {c.competence.replace(/_/g, ' ')}
                                  <span className="opacity-60">({c.niveau})</span>
                                  {c.certifie ? <Award className="w-3 h-3 text-amber-500" title="Certifié" /> : null}
                                  {isAdmin && (
                                    <button onClick={() => handleDeleteCompetence(c.id)} className="ml-1 text-red-400 hover:text-red-600 dark:text-red-400">
                                      <X className="w-3 h-3" />
                                    </button>
                                  )}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination desktop */}
          {empTotalPages > 1 && (
            <div className="hidden md:flex items-center justify-between px-6 py-3 border-t border-border-subtle bg-elevated-1">
              <p className="text-sm text-text-secondary">
                {regularStaff.length} employé{regularStaff.length > 1 ? 's' : ''} — page {empPage}/{empTotalPages}
              </p>
              <div className="flex gap-1">
                <button onClick={() => setEmpPage(p => Math.max(1, p - 1))} disabled={empPage === 1}
                  className="min-h-[44px] min-w-[44px] px-3 rounded text-sm border border-border-strong disabled:opacity-40 hover:bg-elevated-1 transition">&#8249;</button>
                {Array.from({ length: empTotalPages }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setEmpPage(p)}
                    className={`min-h-[44px] min-w-[44px] px-3 rounded text-sm border transition ${p === empPage ? 'bg-blue-500 text-white border-blue-500 dark:border-blue-500' : 'border-border-strong hover:bg-elevated-1'}`}>{p}</button>
                ))}
                <button onClick={() => setEmpPage(p => Math.min(empTotalPages, p + 1))} disabled={empPage === empTotalPages}
                  className="min-h-[44px] min-w-[44px] px-3 rounded text-sm border border-border-strong disabled:opacity-40 hover:bg-elevated-1 transition">&#8250;</button>
              </div>
            </div>
          )}

          {/* Section Extras (desktop) */}
          {extrasStaff.length > 0 && !roleFilter && (
            <div className="bg-surface rounded-xl shadow-lg overflow-hidden hidden md:block">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-3 flex items-center justify-between gap-2">
                <span className="text-white font-bold text-sm">⚡ Intervenants Extra ({extrasStaff.length}) — page {extrasPage}/{extrasTotalPages}</span>
                {extrasTotalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setExtrasPage(p => Math.max(1, p - 1))} disabled={extrasPage === 1}
                      className="px-2 py-1 text-xs bg-white/20 hover:bg-white/30 text-white rounded disabled:opacity-40 disabled:cursor-not-allowed transition"
                      aria-label="Page Extras précédente">← Préc.</button>
                    <button onClick={() => setExtrasPage(p => Math.min(extrasTotalPages, p + 1))} disabled={extrasPage === extrasTotalPages}
                      className="px-2 py-1 text-xs bg-white/20 hover:bg-white/30 text-white rounded disabled:opacity-40 disabled:cursor-not-allowed transition"
                      aria-label="Page Extras suivante">Suiv. →</button>
                  </div>
                )}
              </div>
              <table className="w-full">
                <tbody>
                  {pagedExtrasStaff.map(emp => (
                    <tr key={emp.id} className="border-b border-border-subtle hover:bg-orange-50/30 transition">
                      <td className="py-3 px-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-sm">
                            {emp.avatar}
                          </div>
                          <div>
                            <p className="font-semibold text-text-primary text-sm">{emp.first_name} {emp.last_name}</p>
                            <p className="text-xs text-text-muted truncate">{emp.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-6">
                        <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 rounded-full text-xs font-medium">Extra</span>
                      </td>
                      <td className="py-3 px-6 text-sm text-text-muted italic">Intervenant externe</td>
                      <td className="py-3 px-6 text-sm text-text-muted">{emp.phone || '—'}</td>
                      <td className="py-3 px-6">
                        <div className="flex space-x-1.5">
                          <button onClick={() => setFicheRHEmployeeId(emp.id)}
                            className="text-teal-600 hover:text-teal-700 p-2 hover:bg-teal-50 dark:bg-teal-950/40 rounded transition min-w-[36px] min-h-[36px] flex items-center justify-center" title="Fiche RH">
                            <FileText className="w-4 h-4" />
                          </button>
                          {canEditEmployee(emp) && (
                            <>
                              <button onClick={() => startEdit(emp)} className="text-blue-600 hover:text-blue-700 p-2 hover:bg-blue-50 dark:bg-blue-950/40 rounded transition min-w-[36px] min-h-[36px] flex items-center justify-center" title="Modifier">
                                <Edit2 className="w-4 h-4" />
                              </button>
                              {isAdmin && (
                                <button onClick={() => { setShowPasswordModal(emp); setPasswordMode('email'); setNewPassword(''); setConfirmPassword(''); }}
                                  className="text-amber-600 hover:text-amber-700 p-2 hover:bg-amber-50 dark:bg-amber-950/40 rounded transition min-w-[36px] min-h-[36px] flex items-center justify-center" title="Mot de passe">
                                  <Key className="w-4 h-4" />
                                </button>
                              )}
                              <button onClick={() => setConfirmDeleteModal({ id: emp.id, name: `${emp.first_name} ${emp.last_name}` })} disabled={loading}
                                className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 dark:bg-red-950/40 rounded transition disabled:opacity-50 min-w-[36px] min-h-[36px] flex items-center justify-center" title="Supprimer">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Mobile: Card view */}
          <div className="md:hidden space-y-3">
            {pagedRegularStaff.map(emp => (
              <div key={emp.id} className="bg-surface rounded-lg shadow-sm border p-4 space-y-2">
                <p className="font-semibold text-text-primary">{emp.first_name} {emp.last_name}</p>
                <p className="text-sm text-text-muted">
                  {emp.role_name === 'Extra' ? 'Externe' : emp.service_name}{' '}&middot;{' '}{emp.role_name}
                  {emp.heures_hebdomadaires ? <>{' '}&middot;{' '}{emp.heures_hebdomadaires}h/sem</> : null}
                </p>
                <p className="text-sm text-text-muted truncate">{emp.email}</p>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setFicheRHEmployeeId(emp.id)}
                    className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 px-3 min-h-[44px] bg-teal-50 dark:bg-teal-950/40 rounded-lg transition"
                  >
                    <FileText className="w-4 h-4" />
                    <span>Fiche</span>
                  </button>
                  {canEditEmployee(emp) && (
                    <button
                      onClick={() => startEdit(emp)}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 px-3 min-h-[44px] bg-blue-50 dark:bg-blue-950/40 rounded-lg transition"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                  {canEditEmployee(emp) && isAdmin && (
                    <button
                      onClick={() => {
                        setShowPasswordModal(emp);
                        setPasswordMode('email');
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                      className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 px-3 min-h-[44px] bg-amber-50 dark:bg-amber-950/40 rounded-lg transition"
                    >
                      <Key className="w-4 h-4" />
                    </button>
                  )}
                  {canEditEmployee(emp) && (
                    <button
                      onClick={() => setConfirmDeleteModal({ id: emp.id, name: `${emp.first_name} ${emp.last_name}` })}
                      disabled={loading}
                      className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 px-3 min-h-[44px] bg-red-50 dark:bg-red-950/40 rounded-lg transition disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination mobile */}
          {empTotalPages > 1 && (
            <div className="md:hidden flex items-center justify-center gap-2">
              <button onClick={() => setEmpPage(p => Math.max(1, p - 1))} disabled={empPage === 1}
                className="px-4 min-h-[44px] min-w-[44px] rounded-lg text-sm border border-border-strong disabled:opacity-40 hover:bg-elevated-1 transition">Préc.</button>
              <span className="text-sm text-text-secondary">{empPage} / {empTotalPages}</span>
              <button onClick={() => setEmpPage(p => Math.min(empTotalPages, p + 1))} disabled={empPage === empTotalPages}
                className="px-4 min-h-[44px] min-w-[44px] rounded-lg text-sm border border-border-strong disabled:opacity-40 hover:bg-elevated-1 transition">Suiv.</button>
            </div>
          )}

          {/* Section Extras (mobile) */}
          {extrasStaff.length > 0 && !roleFilter && (
            <div className="md:hidden space-y-3">
              <div className="flex items-center justify-between gap-2 px-1">
                <span className="text-sm font-bold text-orange-700 dark:text-orange-300">Intervenants Extra ({extrasStaff.length}) — p.{extrasPage}/{extrasTotalPages}</span>
                {extrasTotalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setExtrasPage(p => Math.max(1, p - 1))} disabled={extrasPage === 1}
                      className="px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 rounded disabled:opacity-40 min-h-[32px]"
                      aria-label="Page Extras précédente">←</button>
                    <button onClick={() => setExtrasPage(p => Math.min(extrasTotalPages, p + 1))} disabled={extrasPage === extrasTotalPages}
                      className="px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 rounded disabled:opacity-40 min-h-[32px]"
                      aria-label="Page Extras suivante">→</button>
                  </div>
                )}
              </div>
              {pagedExtrasStaff.map(emp => (
                <div key={emp.id} className="bg-surface rounded-lg shadow-sm border border-orange-200 dark:border-orange-800 p-4 space-y-2">
                  <p className="font-semibold text-text-primary">{emp.first_name} {emp.last_name}</p>
                  <p className="text-sm text-text-muted">Intervenant externe &middot; Extra</p>
                  <p className="text-sm text-text-muted truncate">{emp.email}</p>
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setFicheRHEmployeeId(emp.id)}
                      className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 px-3 min-h-[44px] bg-teal-50 dark:bg-teal-950/40 rounded-lg transition"
                    >
                      <FileText className="w-4 h-4" />
                      <span>Fiche</span>
                    </button>
                    {canEditEmployee(emp) && (
                      <button onClick={() => startEdit(emp)} className="flex items-center gap-1 text-sm text-blue-600 px-3 min-h-[44px] bg-blue-50 dark:bg-blue-950/40 rounded-lg transition">
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                    {canEditEmployee(emp) && (
                      <button onClick={() => setConfirmDeleteModal({ id: emp.id, name: `${emp.first_name} ${emp.last_name}` })} disabled={loading}
                        className="flex items-center gap-1 text-sm text-red-600 px-3 min-h-[44px] bg-red-50 dark:bg-red-950/40 rounded-lg transition disabled:opacity-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ===== VUE EMPLOYÉS SUPPRIMÉS ===== */}
      {viewMode === 'deleted' && isAdmin && (
        <div className="bg-surface rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-red-500 to-red-600 px-3 sm:px-6 py-4 flex items-center justify-between">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <UserX className="w-5 h-5" />
              Employés supprimés ({filteredDeleted.length})
            </h3>
            <button
              onClick={loadDeletedEmployees}
              disabled={loadingDeleted}
              className="text-white/80 hover:text-white transition"
            >
              <RefreshCw className={`w-5 h-5 ${loadingDeleted ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {loadingDeleted ? (
            <div className="p-8 text-center text-text-muted">Chargement...</div>
          ) : filteredDeleted.length === 0 ? (
            <div className="p-8 text-center text-text-muted">
              <UserX className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aucun employé supprimé</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredDeleted.map(emp => (
                <div key={emp.id} className="p-4 hover:bg-red-50/30 transition">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-text-secondary font-bold text-sm opacity-60">
                        {emp.avatar || (emp.first_name?.[0] || '') + (emp.last_name?.[0] || '')}
                      </div>
                      <div>
                        <p className="font-semibold text-text-secondary line-through">{emp.first_name} {emp.last_name}</p>
                        <p className="text-sm text-text-muted">{emp.email}</p>
                        <p className="text-xs text-text-muted">
                          Supprimé le {new Date(emp.deleted_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          {emp.deleted_by_first_name && ` par ${emp.deleted_by_first_name} ${emp.deleted_by_last_name}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => handleRestore(emp.id)}
                        disabled={actionLoading}
                        className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700 px-3 py-2 min-h-[44px] bg-green-50 dark:bg-green-950/40 hover:bg-green-100 dark:bg-green-900/40 rounded-lg transition font-medium disabled:opacity-50"
                      >
                        <RotateCcw className="w-4 h-4" />
                        <span>Restaurer</span>
                      </button>
                      <button
                        onClick={() => handleHardDelete(emp.id, `${emp.first_name} ${emp.last_name}`)}
                        disabled={actionLoading}
                        className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 px-3 py-2 min-h-[44px] bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:bg-red-900/40 rounded-lg transition font-medium disabled:opacity-50"
                      >
                        <AlertTriangle className="w-4 h-4" />
                        <span className="hidden sm:inline">Supprimer définitivement</span>
                        <span className="sm:hidden">Supprimer</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== MODALE CHANGEMENT DE MOT DE PASSE — fermeture uniquement via la croix ou Annuler ===== */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface">
            <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <h3 className="text-lg font-bold text-text-primary flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-500" />
                Mot de passe
              </h3>
              <button onClick={() => setShowPasswordModal(null)} className="text-text-muted hover:text-text-secondary p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              <p className="text-sm text-text-secondary">
                Changer le mot de passe de <strong>{showPasswordModal.first_name} {showPasswordModal.last_name}</strong>
              </p>

              {/* Choix du mode */}
              <div className="flex gap-2">
                <button
                  onClick={() => setPasswordMode('email')}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 sm:py-2 min-h-[44px] rounded-lg text-sm font-medium transition ${
                    passwordMode === 'email'
                      ? 'bg-blue-500 text-white'
                      : 'bg-elevated-1 text-text-secondary hover:bg-elevated-1'
                  }`}
                >
                  <Mail className="w-4 h-4" />
                  Envoyer un email
                </button>
                <button
                  onClick={() => setPasswordMode('manual')}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 sm:py-2 min-h-[44px] rounded-lg text-sm font-medium transition ${
                    passwordMode === 'manual'
                      ? 'bg-blue-500 text-white'
                      : 'bg-elevated-1 text-text-secondary hover:bg-elevated-1'
                  }`}
                >
                  <Key className="w-4 h-4" />
                  Définir manuellement
                </button>
              </div>

              {passwordMode === 'email' ? (
                <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    Un email sera envoyé à <strong>{showPasswordModal.email}</strong> avec un lien pour définir un nouveau mot de passe.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Nouveau mot de passe</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Minimum 6 caractères"
                        className="w-full px-4 py-2.5 sm:py-2 pr-10 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-secondary"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Confirmer le mot de passe</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Retapez le mot de passe"
                      className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {confirmPassword && newPassword !== confirmPassword && (
                      <p className="text-xs text-red-500 mt-1">Les mots de passe ne correspondent pas</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
              <button
                onClick={() => setShowPasswordModal(null)}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-gray-200 hover:bg-gray-300 text-text-secondary transition"
              >
                Annuler
              </button>
              <button
                onClick={handleChangePassword}
                disabled={actionLoading || (passwordMode === 'manual' && (!newPassword || newPassword.length < 6 || newPassword !== confirmPassword))}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-amber-500 hover:bg-amber-600 text-white transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {actionLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : passwordMode === 'email' ? (
                  <Mail className="w-4 h-4" />
                ) : (
                  <Key className="w-4 h-4" />
                )}
                <span>{passwordMode === 'email' ? 'Envoyer l\'email' : 'Appliquer'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODALE CONFIRMATION SUPPRESSION DÉFINITIVE ===== */}
      {confirmDeleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={() => setConfirmDeleteModal(null)}>
          <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface" onClick={e => e.stopPropagation()}>
            <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            {/* Header rouge */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-red-500 to-red-600 shrink-0 sm:rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-surface bg-opacity-20 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Suppression définitive</h2>
                  <p className="text-red-100 text-sm">Cette action est irréversible</p>
                </div>
              </div>
              <button onClick={() => setConfirmDeleteModal(null)} className="text-white/70 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Corps scrollable */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              <p className="text-text-primary font-semibold">
                Vous êtes sur le point de supprimer définitivement{' '}
                <span className="text-red-600">{confirmDeleteModal.name}</span>.
              </p>

              <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Toutes les données suivantes seront supprimées :
                </p>
                <ul className="text-sm text-red-600 dark:text-red-400 space-y-1 ml-6 list-disc">
                  <li>Compte utilisateur et mot de passe</li>
                  <li>Pointages, congés, planning</li>
                  <li>Demandes et commentaires</li>
                  <li>Participations aux événements</li>
                  <li>Réservations de véhicules</li>
                  <li>Membres de projets et canaux messagerie</li>
                  <li>Notifications</li>
                </ul>
              </div>
            </div>

            {/* Footer sticky */}
            <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
              <button
                onClick={() => setConfirmDeleteModal(null)}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-gray-200 hover:bg-gray-300 text-text-secondary transition"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  handleDeleteEmployee(confirmDeleteModal.id);
                  setConfirmDeleteModal(null);
                }}
                disabled={loading}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-red-600 hover:bg-red-700 text-white transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modale ajout compétence */}
      {showAddCompModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowAddCompModal(false)}>
          <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface" onClick={e => e.stopPropagation()}>
            <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <h3 className="text-lg font-bold text-text-primary">Ajouter une compétence</h3>
              <button onClick={() => setShowAddCompModal(false)} className="text-text-muted hover:text-text-secondary p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Compétence</label>
                <select value={newCompData.competence} onChange={e => setNewCompData({...newCompData, competence: e.target.value})}
                  className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border rounded-lg">
                  <option value="">— Sélectionner —</option>
                  {competencesRef.filter(cr => !empCompetences.some(ec => ec.competence === cr.nom)).map(cr => (
                    <option key={cr.id} value={cr.nom}>{cr.categorie} — {cr.nom.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Niveau</label>
                <select value={newCompData.niveau} onChange={e => setNewCompData({...newCompData, niveau: e.target.value})}
                  className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border rounded-lg">
                  <option value="debutant">Débutant</option>
                  <option value="intermediaire">Intermédiaire</option>
                  <option value="confirme">Confirmé</option>
                  <option value="expert">Expert</option>
                </select>
              </div>
              <label className="flex items-center gap-2 min-h-[44px]">
                <input type="checkbox" checked={newCompData.certifie} onChange={e => setNewCompData({...newCompData, certifie: e.target.checked})}
                  className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <span className="text-sm text-text-secondary">Certifié</span>
              </label>
            </div>
            <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
              <button onClick={() => setShowAddCompModal(false)} className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center border hover:bg-elevated-1 transition">Annuler</button>
              <button onClick={handleAddCompetence} disabled={!newCompData.competence}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-purple-600 text-white hover:bg-purple-700 transition disabled:opacity-50">Ajouter</button>
            </div>
          </div>
        </div>
      )}
      {/* Modale Fiche RH */}
      {ficheRHEmployeeId && (
        <FicheRHEmployee
          employeeId={ficheRHEmployeeId}
          authToken={authToken}
          authUser={authUser}
          onClose={() => setFicheRHEmployeeId(null)}
        />
      )}
    </div>
  );
};

export default Employees;
